import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { MessageCircle, X, Send, Loader2, CheckCircle2 } from "lucide-react";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("intro"); // intro | chat | sent
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const startChat = () => {
    setError("");
    if (!name.trim() || !/^\S+@\S+\.\S+$/.test(email)) { setError("Please enter your name and a valid email."); return; }
    setStep("chat");
  };

  const send = async () => {
    if (!message.trim()) { setError("Please type a message."); return; }
    setSending(true); setError("");
    try {
      const { data } = await api.post("/support/message", { name: name.trim(), email: email.trim(), message: message.trim() });
      setReply(data.reply);
      setStep("sent");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally { setSending(false); }
  };

  const reset = () => { setMessage(""); setReply(""); setStep("chat"); };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-widget-button"
          className="fixed bottom-20 right-5 md:bottom-5 z-[60] w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-900/40 flex items-center justify-center transition-transform hover:scale-105"
          aria-label="Open support chat"
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-20 right-5 md:bottom-5 z-[60] w-[92vw] max-w-sm rounded-2xl border border-[#27334D] bg-[#0D121C] shadow-2xl shadow-black/50 overflow-hidden animate-fade-up" data-testid="chat-widget-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-[#111726] border-b border-[#1E2A3F]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center"><MessageCircle className="w-4 h-4 text-blue-400" /></div>
              <div>
                <div className="text-sm font-semibold">Customer care</div>
                <div className="text-[10px] text-slate-500 font-mono-niq">We reply within 1 business day</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close" className="p-1.5 rounded-md hover:bg-[#1E2A3F] text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="p-4">
            {step === "intro" && (
              <div className="space-y-3">
                <p className="text-sm text-slate-400">Hi! Leave your details and a message and our team will get back to you.</p>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="chat-name-input" className="w-full bg-[#111726] border border-[#27334D] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" type="email" data-testid="chat-email-input" className="w-full bg-[#111726] border border-[#27334D] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                {error && <p className="text-xs text-red-400">{error}</p>}
                <button onClick={startChat} data-testid="chat-start-button" className="w-full bg-blue-600 hover:bg-blue-500 rounded-lg py-2.5 text-sm font-medium transition-colors">Start chat</button>
              </div>
            )}

            {step === "chat" && (
              <div className="space-y-3">
                <div className="text-xs text-slate-500 font-mono-niq">Chatting as {name} · {email}</div>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="How can we help?" data-testid="chat-message-input" className="w-full bg-[#111726] border border-[#27334D] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500 resize-none" />
                {error && <p className="text-xs text-red-400">{error}</p>}
                <button onClick={send} disabled={sending} data-testid="chat-send-button" className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 rounded-lg py-2.5 text-sm font-medium transition-colors disabled:opacity-60">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send</>}
                </button>
              </div>
            )}

            {step === "sent" && (
              <div className="space-y-4 text-center py-2" data-testid="chat-sent-state">
                <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
                <p className="text-sm text-slate-300 leading-relaxed">{reply}</p>
                <button onClick={reset} className="text-xs text-blue-400 hover:text-blue-300">Send another message</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
